import * as React from "react";

/**
 * Toaster — minimal toast-stack ohne externe deps.
 * Phase 11.4 erweitert das auf shadcn/radix-toast wenn nötig; bis dahin
 * reichen die window-events der app (sw-update, outbox-abandoned).
 */

interface ToastItem {
  id: number;
  message: string;
  kind: "info" | "success" | "warning" | "danger";
  durationMs: number;
}

export function Toaster(): React.ReactElement {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);

  React.useEffect(() => {
    let counter = 0;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{
        message: string;
        kind?: ToastItem["kind"];
        durationMs?: number;
      }>).detail;
      if (!detail?.message) return;
      counter += 1;
      const id = counter;
      setToasts((prev) => [
        ...prev,
        {
          id,
          message: detail.message,
          kind: detail.kind ?? "info",
          durationMs: detail.durationMs ?? 5000,
        },
      ]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, detail.durationMs ?? 5000);
    };
    window.addEventListener("hitzeprotokoll:toast", handler);
    return () => window.removeEventListener("hitzeprotokoll:toast", handler);
  }, []);

  if (toasts.length === 0) return <></>;

  return (
    <div className="fixed bottom-20 left-1/2 z-[var(--z-toast)] flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          role={t.kind === "danger" ? "alert" : "status"}
          className={kindClasses(t.kind)}
        >
          {t.message}
        </div>
      ))}
    </div>
  );
}

function kindClasses(kind: ToastItem["kind"]): string {
  const base = "rounded-md px-4 py-3 text-sm font-medium shadow-[var(--shadow-md)]";
  switch (kind) {
    case "success":
      return `${base} bg-[color:var(--color-cool-teal-600)] text-white`;
    case "warning":
      return `${base} bg-[color:var(--color-heat-3)] text-white`;
    case "danger":
      return `${base} bg-[color:var(--color-heat-4)] text-white`;
    default:
      return `${base} bg-[color:var(--color-neutral-900)] text-white`;
  }
}

/** Convenience helper für die feature-mutations. */
export function toast(message: string, kind: ToastItem["kind"] = "info", durationMs?: number): void {
  window.dispatchEvent(
    new CustomEvent("hitzeprotokoll:toast", {
      detail: { message, kind, durationMs },
    })
  );
}
